// ShowConsultationPDF 07/05/2025 - Com margens internas ajustadas e molduras proporcionais

import jsPDF from 'jspdf';
import headerImg from '../../assets/img/headerImg.png';
import fruitLogo from '../../assets/img/fruitLogo.png';
import allFoodsLogo from '../../assets/img/shortcut_icon/allFoods.png';

import {
  calculateAgeOnlyYear,
  formatDate,
  formatAnyDate,
  formatTime,
  formatGender,
  formatStatus,
  formatDietName,
} from '../Functions/formatPersons';
import {
  anthropometryBold1,
  dietBold1
} from '../Functions/formatConsultationPDF';

const getBase64ImageFromURL = async (url) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const currentYear = new Date().getFullYear();

const showConsultationPdf = async (
  consultationData,
  patientData,
  dietData,
  anthropometryData,
  preview = false
) => {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageHeight = pdf.internal.pageSize.height;
  let y = 30;
  let page = 1;

  const headerImgData = await getBase64ImageFromURL(headerImg);
  const fruitImgData = await getBase64ImageFromURL(fruitLogo);
  const allFoodsImgData = await getBase64ImageFromURL(allFoodsLogo);

  const addHeader = () => {
    pdf.addImage(headerImgData, 'PNG', 0, 0, 210, 20);
  };

  const addFooter = () => {
    const footerY = pageHeight - 5;
    pdf.setLineWidth(0.4);
    pdf.setDrawColor(0);
    pdf.line(1, footerY - 4, 209, footerY - 3.5);
    pdf.setFontSize(6);
    pdf.text(`© ${currentYear}`, 100, footerY + 0.5, 'center');
    pdf.setFontSize(7);
    pdf.text('Nutrição App', 118, footerY + 0.5, 'center');
    pdf.addImage(fruitImgData, 'PNG', 105, footerY - 2, 5, 3.5);
    pdf.text(`${page}`, 205, footerY, 'center');
  };

  const checkSpace = (extra = 6) => {
    if (y + extra > pageHeight - 15) {
      addFooter();
      pdf.addPage();
      page++;
      y = 30;
      addHeader();
    }
  };

  const addSectionBox = (title, contentCallback) => {
    const boxMargin = 3;
    const boxPaddingLeft = 14;
    const boxTopY = y;
    checkSpace(12);

    pdf.setTextColor(139, 197, 64);
    pdf.setFontSize(9);
    pdf.setFont('Helvetica', 'bold');
    pdf.text(title, boxPaddingLeft, y);
    y += 6;
    pdf.setTextColor(0);
    pdf.setFontSize(8);
    pdf.setFont('Helvetica', 'normal');

    const contentStartY = y;
    const originalY = y;

    contentCallback();

    const boxBottomY = y;
    const boxHeight = boxBottomY - boxTopY;
    pdf.setDrawColor(139, 197, 64);
    pdf.setLineWidth(0.2);
    pdf.roundedRect(10, boxTopY - boxMargin, 190, boxHeight + boxMargin * 2, 3, 3);
    y = boxBottomY + 10;
  };

  const addLine = (text, options = {}) => {
    checkSpace();
    const x = options.x || 14;
    const maxWidth = options.maxWidth || 182;
    const lines = pdf.splitTextToSize(text, maxWidth);
    lines.forEach(line => {
      checkSpace(5);
      pdf.text(line, x, y);
      y += 5;
    });
  };

  addHeader();

  addSectionBox('Consulta', () => {
    addLine(`Data da Consulta: ${formatDate(consultationData.consult_date)}`);
    addLine(`Status: ${formatStatus(consultationData.status) || 'N/A'}`);
    addLine(`Profissional: ${consultationData.professional_name || 'N/A'}`);
  });

  addSectionBox('Paciente', () => {
    addLine(`Nome: ${patientData.first_name} ${patientData.last_name}`);
    addLine(`Idade: ${calculateAgeOnlyYear(patientData.birth_date)} Anos  Gênero: ${formatGender(patientData.gender)}  CPF: ${patientData.cpf || 'N/A'}`);
    addLine(`Telefone: ${patientData.mobile || 'N/A'} | E-mail: ${patientData.email || 'N/A'}`);
  });

  addSectionBox('Consultas Prontuário', () => {
    addLine(`Doenças Atuais: ${consultationData.current_illnesses || 'N/A'}`);
    addLine(`Doenças Pré-existentes: ${consultationData.past_illnesses || 'N/A'}`);
    addLine(`Alergias e Intolerâncias: ${consultationData.allergies || 'N/A'}`);
    addLine(`Histórico de Cirurgias: ${consultationData.surgeries || 'N/A'}`);
    addLine(`Medicamentos: ${consultationData.medications || 'N/A'}`);
    addLine(`Histórico Familiar: ${consultationData.family_history || 'N/A'}`);
  });

  addSectionBox('Anamnese', () => {
    addLine(`Objetivo da Consulta: ${consultationData.objectiveQ1 || 'N/A'}`);
    addLine(`Meta Específica: ${consultationData.objectiveQ2 || 'N/A'}`);
    addLine(`Dificuldade com a alimentação: ${consultationData.objectiveQ3 || 'N/A'}`);
    addLine(`Quantas refeições ao dia: ${consultationData.foodHistoryQ1 || 'N/A'}`);
    addLine(`Costuma pular refeições: ${consultationData.foodHistoryQ2 || 'N/A'}`);
    addLine(`Café/Almoço/Jantar: ${consultationData.foodHistoryQ3 || 'N/A'}`);
    addLine(`Lanches entre refeições: ${consultationData.foodHistoryQ4 || 'N/A'}`);
    addLine(`Doces ou salgados: ${consultationData.foodHistoryQ5 || 'N/A'}`);
    addLine(`Fast food e ultraprocessados: ${consultationData.foodHistoryQ6 || 'N/A'}`);
    addLine(`Restrições alimentares: ${consultationData.foodHistoryQ7 || 'N/A'}`);
    addLine(`Prepara suas refeições: ${consultationData.foodHistoryQ8 || 'N/A'}`);
    addLine(`Comer fora ou delivery: ${consultationData.foodHistoryQ9 || 'N/A'}`);
    addLine(`Litros de água por dia: ${consultationData.hydrationQ1 || 'N/A'}`);
    addLine(`Outras bebidas: ${consultationData.hydrationQ2 || 'N/A'}`);
    addLine(`Sente sede constante: ${consultationData.hydrationQ3 || 'N/A'}`);
  });

  addSectionBox('Antropometria', () => {
    if (Array.isArray(anthropometryData) && anthropometryData.length > 0) {
      anthropometryBold1(pdf, anthropometryData[0], y, 6, formatAnyDate);
      y += 36;
    } else {
      addLine('Sem dados de antropometria disponíveis.');
    }
  });

  addSectionBox('Dietas', () => {
    if (Array.isArray(dietData) && dietData.length > 0) {
      dietData.forEach((diet, index) => {
        addLine(`Dieta ${index + 1}: ${formatDietName(diet.name) || 'N/A'}`);
        addLine(`Objetivo: ${diet.objective || 'N/A'}`);
        addLine(`Calorias Totais: ${diet.diet_total_calories || 'N/A'} kcal/dia`);
        diet.meals?.forEach((meal, idx) => {
          checkSpace(10);
          dietBold1(pdf, idx, meal, y, formatTime);
          y += 5;
          meal.foods?.forEach((food, i) => {
            addLine(`• ${food.food_name || 'N/A'} - ${food.quantity} ${food.unit_measurement} - ${food.calories || 0} kcal`);
          });
        });
      });
    } else {
      addLine('Sem dietas cadastradas.');
    }
  });

  addFooter();

  const blob = pdf.output('blob');
  if (preview) {
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  }
  return blob;
};

export default showConsultationPdf;
